import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { classes } from 'typestyle';
import Checkbox from '../../layout/checkbox/checkbox.component';
import {
    taxonomyListCheckboxContainerNameClassName,
    taxonomyListClassName,
    taxonomyListGroupedBodyClassName,
    taxonomyListGroupedBodyLeftClassName,
    taxonomyListGroupedBodyRightClassName,
    taxonomyListGroupedCoreClassName,
    taxonomyListGroupedGroupClassName,
    taxonomyListGroupedLetterButtonClassName,
    taxonomyListGroupedLetterClassName,
    taxonomyListGroupedLettersClassName,
    taxonomyListGroupedLettersMovingClassName,
    taxonomyListListClassName,
    taxonomyListListItemClassName,
    taxonomyListNameClassName,
    taxonomyListNameClickableClassName,
    taxonomyListNameStrikethroughClassName,
    taxonomyListSectionClassName,
    taxonomyListGroupedClassName,
    taxonomyListNameMaxSelectedDisabledClassName
} from './taxonomy-list-filter.style';
import {
    TaxonomyListGroupedProps,
    TaxonomyListFilterRef,
} from './taxonomy-list-filter.types';
import { groupTaxonomies } from './taxonomy-list-filter.utils';

const TaxonomyListGrouped = forwardRef<TaxonomyListFilterRef, TaxonomyListGroupedProps>(({
    list,
    selectedIds,
    onChange,
    children,
    onClickItem,
    maxSelections,
    strikethroughIds,
}, ref) => {
    const [groupOfTaxonomies, letters] = groupTaxonomies(list);
    const elementRef = useRef<HTMLDivElement>(null);
    const scrollElementRef = useRef<HTMLDivElement>(null);
    const verticalLettersWrapperRef = React.createRef<HTMLDivElement>();
    const verticalLettersMovingRef = React.createRef<HTMLDivElement>();

    const maxSelected: boolean = (selectedIds && onChange && maxSelections)
        ? selectedIds.length >= maxSelections
        : false

    useImperativeHandle(ref, (): TaxonomyListFilterRef => ({
        scrollToTop: () => {
            if (scrollElementRef.current) {
                scrollElementRef.current.scrollTop = 0;
            }
        },
    }));

    const handleClickLetterButton = (letter: string) => {
        if (elementRef.current && scrollElementRef.current) {
            const targetElement = elementRef.current.querySelector<HTMLDivElement>(`[data-letter="${letter}"]`);
            
            if (targetElement) {
                scrollElementRef.current.scrollTo({
                    top: targetElement.offsetTop,
                    behavior: 'smooth',
                });
            }
        }
    };

    const handleScrollCore = () => {
        if (
            scrollElementRef.current
            && verticalLettersWrapperRef.current
            && verticalLettersMovingRef.current
        ) {
            const lettersWrapperHeight = verticalLettersWrapperRef.current.offsetHeight;
            const lettersMovingHeight = verticalLettersMovingRef.current.offsetHeight;

            if (lettersMovingHeight > lettersWrapperHeight) {
                const maxNegativeScrollAdjustment = lettersWrapperHeight - lettersMovingHeight;
                const currentNegativeContentScroll = Math.max(-scrollElementRef.current.scrollTop, maxNegativeScrollAdjustment);

                verticalLettersMovingRef.current.style.top = `${currentNegativeContentScroll}px`;
                
            }
        }
    };

    const handleChangeCheckbox = (
        isSelected: boolean,
        selectedIds: number[],
        taxonomyDataId: number
    ) => {
        if (onChange) {
            if (isSelected) {
                if (maxSelected) {
                    return;
                }

                onChange(
                    [...selectedIds, taxonomyDataId]
                );
            }
            else {
                onChange(
                    selectedIds.filter(id => id !== taxonomyDataId)
                );
            }
        }
    };

    return (
        <div className={ classes(taxonomyListClassName, taxonomyListGroupedClassName) } ref={ elementRef }>
            { children && (
                <div className={ taxonomyListSectionClassName }>
                    { children }
                </div>
            ) }
            <div className={ taxonomyListGroupedBodyClassName }>
                <div className={ taxonomyListGroupedBodyLeftClassName }>
                    <div className={ taxonomyListGroupedLettersClassName } ref={ verticalLettersWrapperRef }>
                        <div className={ taxonomyListGroupedLettersMovingClassName } ref={ verticalLettersMovingRef }>
                            { letters.map((letter, index) => (
                                <div
                                    key={ index }
                                    className={ taxonomyListGroupedLetterButtonClassName }
                                    onClick={ () => handleClickLetterButton(letter) }
                                    children={ letter }/>
                            )) }
                        </div>
                    </div>
                </div>
                <div className={ taxonomyListGroupedBodyRightClassName }>
                    <div data-list className={ taxonomyListGroupedCoreClassName } ref={ scrollElementRef } onScroll={ handleScrollCore }>
                        { groupOfTaxonomies.map((taxonomyGroup, i) => (
                            <div key={ i } className={ taxonomyListGroupedGroupClassName }>
                                <div className={ taxonomyListGroupedLetterClassName } data-letter={ taxonomyGroup.letter  }>
                                    { taxonomyGroup.letter }
                                </div>
                                <ul className={ taxonomyListListClassName }>
                                    { taxonomyGroup.taxonomies.map((taxonomyData, index) => {
                                        const isSelected = Boolean(selectedIds && selectedIds.indexOf(taxonomyData.id) !== -1);

                                        return (
                                            <li key={ index } className={ taxonomyListListItemClassName }>
                                                { selectedIds && (
                                                    <div className={ taxonomyListCheckboxContainerNameClassName }>
                                                        <Checkbox
                                                            value={ isSelected }
                                                            disabled={ maxSelected && !isSelected }
                                                            onChange={ newIsSelected => handleChangeCheckbox(
                                                                newIsSelected, selectedIds, taxonomyData.id
                                                            ) }/>
                                                    </div>
                                                ) }
                                                <div className={ classes(
                                                    taxonomyListNameClassName,
                                                    maxSelected && !isSelected && taxonomyListNameMaxSelectedDisabledClassName
                                                ) }>
                                                    <span className={ classes(
                                                        taxonomyListNameClickableClassName,
                                                        (strikethroughIds && !!strikethroughIds.length)
                                                        && (strikethroughIds.includes(taxonomyData.id))
                                                        && (
                                                            taxonomyListNameStrikethroughClassName
                                                        )
                                                    )} onClick={ event => {
                                                        event.preventDefault();

                                                        if (selectedIds) {
                                                            handleChangeCheckbox(
                                                                !isSelected,
                                                                selectedIds,
                                                                taxonomyData.id
                                                            );
                                                        }

                                                        onClickItem && onClickItem(taxonomyData);
                                                    } }>
                                                        { taxonomyData.name }
                                                    </span>
                                                </div>
                                            </li>
                                        )
                                    }) }
                                </ul>
                            </div>
                        )) }
                    </div>
                </div>
            </div>
        </div>
    )
});

export default TaxonomyListGrouped;
